import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User, Mail, Phone, Shield, Award, Clock, KeyRound,
  CheckCircle2, AlertCircle, Pencil, X, Save, Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { profileApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { clsx } from 'clsx';

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}

const ROLE_LABEL: Record<string, string> = {
  hospital_admin: 'Hospital Admin',
  owner:          'Owner',
  doctor:         'Doctor',
  nurse:          'Nurse',
  receptionist:   'Receptionist',
  lab_technician: 'Lab Technician',
  pharmacist:     'Pharmacist',
  billing_staff:  'Billing Staff',
  accountant:     'Accountant',
  radiologist:    'Radiologist',
};

const ROLE_COLOR: Record<string, string> = {
  hospital_admin: 'bg-purple-100 text-purple-700',
  owner:          'bg-indigo-100 text-indigo-700',
  doctor:         'bg-blue-100 text-blue-700',
  nurse:          'bg-teal-100 text-teal-700',
  receptionist:   'bg-green-100 text-green-700',
  lab_technician: 'bg-yellow-100 text-yellow-700',
  pharmacist:     'bg-orange-100 text-orange-700',
  billing_staff:  'bg-pink-100 text-pink-700',
  accountant:     'bg-rose-100 text-rose-700',
  radiologist:    'bg-cyan-100 text-cyan-700',
};

interface ProfileData {
  id: string;
  name: string;
  email: string;
  mobile: string | null;
  role: string;
  additional_roles: string | null;
  specialization: string | null;
  reg_number: string | null;
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

export default function ProfilePage() {
  const qc          = useQueryClient();
  const storeUser   = useAuthStore(s => s.user);

  const { data: profile, isLoading, isError, error } = useQuery<ProfileData>({
    queryKey: ['my-profile'],
    queryFn:  () => profileApi.get().then(r => r.data),
  });

  // ── Edit basic info ───────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editName,   setEditName]   = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editError,  setEditError]  = useState('');

  useEffect(() => {
    if (profile) {
      setEditName(profile.name);
      setEditMobile(profile.mobile ?? '');
    }
  }, [profile]);

  const updateMut = useMutation({
    mutationFn: (data: { name?: string; mobile?: string }) => profileApi.update(data),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['my-profile'] });
      setEditing(false);
      setEditError('');
    },
    onError: () => setEditError('Failed to save. Please try again.'),
  });

  function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editName.trim() || editName.trim().length < 2) {
      setEditError('Name must be at least 2 characters.');
      return;
    }
    if (editMobile && !/^\d{10}$/.test(editMobile)) {
      setEditError('Mobile must be exactly 10 digits.');
      return;
    }
    setEditError('');
    updateMut.mutate({
      name:   editName.trim(),
      mobile: editMobile || undefined,
    });
  }

  // ── Change password ───────────────────────────────────────────────
  const [pwOpen,    setPwOpen]    = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew,     setPwNew]     = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError,   setPwError]   = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  const pwMut = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      profileApi.changePassword(data),
    onSuccess: () => {
      setPwSuccess(true);
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
      setTimeout(() => { setPwSuccess(false); setPwOpen(false); }, 2000);
    },
    onError: (err: any) => {
      setPwError(err?.response?.data?.error ?? 'Failed to change password.');
    },
  });

  function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    if (pwNew.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; }
    pwMut.mutate({ current_password: pwCurrent, new_password: pwNew });
  }

  const additionalRoles: string[] = (() => {
    try { return JSON.parse(profile?.additional_roles ?? '[]'); } catch { return []; }
  })();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 size={28} className="animate-spin"/>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
        <AlertCircle size={32} className="text-red-400"/>
        <p className="text-sm">Failed to load profile. Please refresh the page.</p>
        <p className="text-xs text-gray-300">{String((error as any)?.response?.data?.error ?? '')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-navy">My Profile</h1>

      {/* ── Identity card ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-orange/15 flex items-center justify-center
                          text-orange text-2xl font-bold select-none flex-shrink-0">
            {initials(profile.name)}
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-navy truncate">{profile.name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={clsx(
                'px-2.5 py-0.5 rounded-full text-xs font-semibold',
                ROLE_COLOR[profile.role] ?? 'bg-gray-100 text-gray-600'
              )}>
                {ROLE_LABEL[profile.role] ?? profile.role}
              </span>
              {additionalRoles.map(r => (
                <span key={r} className={clsx(
                  'px-2.5 py-0.5 rounded-full text-xs font-medium border',
                  'bg-gray-50 text-gray-500 border-gray-200'
                )}>
                  {ROLE_LABEL[r] ?? r}
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-400 mt-1">{storeUser?.hospitalName}</p>
          </div>
        </div>
      </div>

      {/* ── Personal info ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy">Personal Information</h3>
          {!editing ? (
            <button onClick={() => { setEditing(true); setEditError(''); }}
              className="flex items-center gap-1.5 text-sm text-navy/70 hover:text-navy px-3 py-1.5 rounded-lg hover:bg-gray-50 border border-gray-200">
              <Pencil size={13}/> Edit
            </button>
          ) : (
            <button onClick={() => { setEditing(false); setEditError(''); setEditName(profile.name); setEditMobile(profile.mobile ?? ''); }}
              className="text-gray-400 hover:text-gray-600">
              <X size={18}/>
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={submitEdit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Full Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                placeholder="Your full name"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Mobile</label>
              <input value={editMobile} onChange={e => setEditMobile(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                placeholder="10-digit mobile number" maxLength={10} inputMode="numeric"/>
            </div>
            {editError && <p className="text-red-500 text-sm">{editError}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={() => setEditing(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={updateMut.isPending}
                className="flex-1 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-50 flex items-center justify-center gap-2">
                {updateMut.isPending ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Save size={14}/> Save Changes</>}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <InfoRow icon={User}   label="Name"   value={profile.name}/>
            <InfoRow icon={Mail}   label="Email"  value={profile.email}/>
            <InfoRow icon={Phone}  label="Mobile" value={profile.mobile ?? '—'}/>
            {profile.specialization && (
              <InfoRow icon={Award} label="Specialization" value={profile.specialization}/>
            )}
            {profile.reg_number && (
              <InfoRow icon={Shield} label="Reg. Number" value={profile.reg_number}/>
            )}
          </div>
        )}
      </div>

      {/* ── Change password ────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy">Password</h3>
          {!pwOpen && (
            <button onClick={() => { setPwOpen(true); setPwError(''); setPwSuccess(false); }}
              className="flex items-center gap-1.5 text-sm text-navy/70 hover:text-navy px-3 py-1.5 rounded-lg hover:bg-gray-50 border border-gray-200">
              <KeyRound size={13}/> Change Password
            </button>
          )}
        </div>

        {!pwOpen ? (
          <p className="text-sm text-gray-400">Use a strong password that you don't use elsewhere.</p>
        ) : pwSuccess ? (
          <div className="flex items-center gap-2 text-green-600 py-2">
            <CheckCircle2 size={18}/>
            <span className="text-sm font-medium">Password changed successfully!</span>
          </div>
        ) : (
          <form onSubmit={submitPassword} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Current Password</label>
              <input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                placeholder="Enter current password" autoComplete="current-password"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">New Password</label>
              <input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                placeholder="Minimum 8 characters" autoComplete="new-password"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Confirm New Password</label>
              <input type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                placeholder="Repeat new password" autoComplete="new-password"/>
            </div>
            {pwError && (
              <div className="flex items-center gap-2 text-red-500 text-sm">
                <AlertCircle size={14}/> {pwError}
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => { setPwOpen(false); setPwError(''); setPwCurrent(''); setPwNew(''); setPwConfirm(''); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={!pwCurrent || !pwNew || !pwConfirm || pwMut.isPending}
                className="flex-1 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-50 flex items-center justify-center gap-2">
                {pwMut.isPending ? <><Loader2 size={14} className="animate-spin"/> Updating…</> : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Account info ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6">
        <h3 className="font-semibold text-navy mb-4">Account Details</h3>
        <div className="space-y-3">
          <InfoRow
            icon={Shield}
            label="MFA Status"
            value={profile.mfa_enabled ? 'Enabled' : 'Not enabled'}
            valueClass={profile.mfa_enabled ? 'text-green-600 font-medium' : 'text-gray-400'}
          />
          <InfoRow
            icon={Clock}
            label="Last Login"
            value={profile.last_login_at
              ? format(new Date(profile.last_login_at), 'dd MMM yyyy, hh:mm a')
              : 'Never'}
          />
          <InfoRow
            icon={User}
            label="Account Created"
            value={format(new Date(profile.created_at), 'dd MMM yyyy')}
          />
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  icon: Icon, label, value, valueClass,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
        <Icon size={14} className="text-gray-400"/>
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-400">{label}</div>
        <div className={clsx('text-sm text-gray-800 font-medium truncate', valueClass)}>{value}</div>
      </div>
    </div>
  );
}
